import { css } from "@emotion/css";
import { useEffect, useState, useRef } from "react";
import type {
  SearchFieldProps,
  ValidationResult,
  SearchFieldRenderProps,
} from "react-aria-components";
import { SearchField } from "react-aria-components";
import { useDebounce } from "usehooks-ts";

import { Input } from "../../internal/Input";

interface Props extends SearchFieldProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
  compact?: boolean;
  testID: string;
  className?: string | ((values: SearchFieldRenderProps) => string) | undefined;
  maxWidth?: string;
  debounce?: number;
  handleChange?: () => void;
}

export function Search(props: Props) {
  const {
    label,
    description,
    errorMessage,
    placeholder,
    compact = true,
    maxWidth = "416px",
    testID,
    value: propValue,
    debounce = 300,
    onChange,
    handleChange,
    ...searchFieldProps
  } = props;
  const [value, setValue] = useState(propValue ?? "");
  const searchFilterWithDebounce = useDebounce(value, debounce);

  // Track the last propValue to detect external changes
  const lastPropValueRef = useRef(propValue);

  const handleSearchChange = (newValue: string) => {
    setValue(newValue);
    handleChange && handleChange();
  };

  // Sync with propValue changes from parent (controlled component)
  // Only sync when propValue actually changes externally, not from our own onChange
  useEffect(() => {
    if (propValue !== lastPropValueRef.current) {
      setValue(propValue ?? "");
      lastPropValueRef.current = propValue;
    }
  }, [propValue]);

  // Only call onChange when the debounced value changes
  // Use a ref to track the last emitted value to prevent duplicate calls
  const lastEmittedValueRef = useRef(searchFilterWithDebounce);

  useEffect(() => {
    if (searchFilterWithDebounce !== lastEmittedValueRef.current) {
      onChange?.(searchFilterWithDebounce);
      lastEmittedValueRef.current = searchFilterWithDebounce;
    }
  }, [searchFilterWithDebounce, onChange]);

  return (
    <SearchField
      className={css({ maxWidth }, styles.field)}
      {...searchFieldProps}
      data-testid={testID}
    >
      <Input
        onChange={(e) => {
          handleSearchChange(e.target.value);
        }}
        onClear={() => {
          handleSearchChange("");
        }}
        value={value}
        leftIcon="search"
        placeholder={placeholder}
        clearable={Boolean(value?.length)}
        compact={compact}
        data-testid={`${testID}-input`}
      />
    </SearchField>
  );
}

const styles = {
  field: css({
    display: "flex",
    width: "100%",
    minWidth: 80,
  }),
};
